import { useCallback } from "react";

/** Default max upload size (bytes). Override per usage via hook options or modal props. */
export const DEFAULT_DOCUMENT_MAX_BYTES = 12 * 1024 * 1024;

/**
 * @param {number} bytes
 * @returns {string} e.g. "15 MB"
 */
export function formatMaxFileSizeLabel(bytes) {
  const mb = Math.round(bytes / (1024 * 1024));
  return `${mb} MB`;
}

/**
 * Shared file → base64 + preview wiring for document modals (react-hook-form).
 *
 * @param {object} options
 * @param {number} [options.maxBytes=DEFAULT_DOCUMENT_MAX_BYTES]
 * @param {import('react-hook-form').UseFormSetValue<any>} options.setValue
 * @param {React.Dispatch<React.SetStateAction<string|null>>} options.setPreviewUrl
 * @param {import('react-hook-form').UseFormSetError<any>} options.setError
 * @param {import('react-hook-form').UseFormClearErrors<any>} options.clearErrors
 * @param {string} [options.base64Field='fileBase64']
 * @param {string} [options.fileNameField='fileName']
 */
export function useDocumentFileUpload({
    maxBytes = DEFAULT_DOCUMENT_MAX_BYTES,
    setValue,
    setPreviewUrl,
    setError,
    clearErrors,
    base64Field = "fileBase64",
    fileNameField = "fileName",
}) {
    const applyFile = useCallback(
        (file) => {
            if (!file) return;

            if (file.size > maxBytes) {
                setError(base64Field, {
                    type: "manual",
                    message: `File size exceeds ${formatMaxFileSizeLabel(maxBytes)} limit`,
                });
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setValue(base64Field, reader.result);
                setValue(fileNameField, file.name);
                setPreviewUrl(reader.result);
                clearErrors([base64Field, fileNameField]);
            };
            reader.readAsDataURL(file);
        },
        [maxBytes, setValue, setPreviewUrl, setError, clearErrors, base64Field, fileNameField],
    );

    const handleFileChange = useCallback(
        (e) => {
            applyFile(e.target.files?.[0]);
            e.target.value = "";
        },
        [applyFile],
    );

    const handleFileDrop = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            applyFile(e.dataTransfer?.files?.[0]);
        },
        [applyFile],
    );

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const clearFile = useCallback(() => {
        setValue(base64Field, "");
        setValue(fileNameField, "");
        setPreviewUrl(null);
    }, [setValue, setPreviewUrl, base64Field, fileNameField]);

    return {
        handleFileChange,
        handleFileDrop,
        handleDragOver,
        clearFile,
        maxBytes,
    };
}
